import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useGetTourOffer, useGetTourOfferSummary, useSaveTourOffer } from '~/hooks/apis/tours/useTourOffers';

import { Bundle, TourBundleComponent } from '~/types/bundles/bundle';
import {
  ACTIVITY_LEVEL_LABELS,
  DEPOSIT_TYPE_LABELS,
  EMPTY_TOUR_OFFER_BOOKING_TERMS,
  EMPTY_TOUR_OFFER_CONTENT,
  GUIDE_TYPE_LABELS,
  TOUR_STYLE_LABELS,
  TourOffer,
  TourOfferBookingTerms,
  TourOfferConfigurationContent,
  TourOfferContent,
} from '~/types/tours/tourOffer';

import ConfigurationEditor, { cleanConfigurationContent, isConfigurationContentValid } from './ConfigurationEditor';
import FinePrintEditor from './FinePrintEditor';

interface Props {
  bundle: Bundle;
}

interface Form {
  name: string;
  content: TourOfferContent;
  bookingTerms: TourOfferBookingTerms;
  /** By configuration id. */
  configurations: Record<string, TourOfferConfigurationContent>;
}

const configurationsOf = (offer: TourOffer) => offer.services.flatMap((service) => service.configurations);

const formFrom = (offer: TourOffer): Form => ({
  name: offer.name ?? '',
  content: offer.content ?? EMPTY_TOUR_OFFER_CONTENT,
  bookingTerms: offer.bookingTerms ?? EMPTY_TOUR_OFFER_BOOKING_TERMS,
  configurations: Object.fromEntries(
    configurationsOf(offer).map((configuration) => [
      configuration.id,
      {
        title: configuration.content?.title ?? null,
        description: configuration.content?.description ?? null,
        inclusions: configuration.content?.inclusions ?? [],
        itineraries: configuration.itineraries.map(({ id, title, text, accommodation, meals }) => ({
          id,
          title,
          text,
          accommodation,
          meals,
        })),
      },
    ]),
  ),
});

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
const nullIfEmpty = (value: string) => (value.trim() === '' ? null : value);
const numberOrNull = (value: string) => (value.trim() === '' ? null : Number(value));

function Section({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6">{title}</Typography>
        {description && (
          <Typography variant="body2" color="text.secondary" mb={2}>
            {description}
          </Typography>
        )}
        <Box mt={description ? 0 : 2}>{children}</Box>
      </CardContent>
    </Card>
  );
}

function enumField<T extends string>(
  label: string,
  labels: Record<T, string>,
  value: T | null,
  onChange: (value: T | null) => void,
) {
  return (
    <TextField
      select
      label={label}
      value={value ?? ''}
      onChange={(event) => onChange((event.target.value || null) as T | null)}
      fullWidth
    >
      <MenuItem value="">
        <em>Not set</em>
      </MenuItem>
      {(Object.keys(labels) as Array<T>).map((key) => (
        <MenuItem key={key} value={key}>
          {labels[key]}
        </MenuItem>
      ))}
    </TextField>
  );
}

/** The package content customers see, saved on the bundle's LTLE in svc-tour. */
export default function BundleContentStep({ bundle }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const tour = bundle.components.find(
    (component): component is TourBundleComponent => component.product_type === 'tour',
  );
  const { data: offer, isLoading, error } = useGetTourOffer(tour?.tour_offer_id);
  // The same flag the step bar unlocks this step on, so the two never disagree.
  const { data: summary, isLoading: isLoadingSummary } = useGetTourOfferSummary(tour?.tour_offer_id);
  const saveOffer = useSaveTourOffer(tour?.tour_offer_id ?? '');
  const [form, setForm] = useState<Form | null>(null);

  // Start from the saved offer once it loads; background refetches leave unsaved edits alone.
  useEffect(() => {
    if (offer) setForm(formFrom(offer));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer?.id]);

  const isLtle = !!summary?.isLtle;

  if (!tour) {
    return <Alert severity="info">Add the tour first.</Alert>;
  }
  if (isLoading || isLoadingSummary || (offer && !form)) {
    return (
      <Box p={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error">{error.message}</Alert>;
  }
  if (!offer || !isLtle) {
    return (
      <Alert severity="info">
        Content is edited on the bundle's LTLE, which is created when the bundle is approved.
      </Alert>
    );
  }

  const saved = formFrom(offer);
  const configurations = configurationsOf(offer);
  const changedConfigurationIds = configurations
    .map((configuration) => configuration.id)
    .filter((id) => !same(cleanConfigurationContent(form.configurations[id]), saved.configurations[id]));
  const changed = {
    name: form.name.trim() !== saved.name,
    content: !same(form.content, saved.content),
    bookingTerms: !same(form.bookingTerms, saved.bookingTerms),
  };
  const changeCount = Object.values(changed).filter(Boolean).length + changedConfigurationIds.length;
  const invalid =
    !form.name.trim() ||
    changedConfigurationIds.some((id) => !isConfigurationContentValid(form.configurations[id])) ||
    form.content.finePrint.some((item) => !item.title.trim() || !item.body.trim()) ||
    [form.bookingTerms.depositAmount, form.bookingTerms.depositDueDays, form.bookingTerms.finalPaymentDueDays].some(
      (value) => value !== null && (Number.isNaN(value) || value < 0),
    );

  const setContent = (change: Partial<TourOfferContent>) =>
    setForm((current) => ({ ...current, content: { ...current.content, ...change } }));
  const setBookingTerms = (change: Partial<TourOfferBookingTerms>) =>
    setForm((current) => ({ ...current, bookingTerms: { ...current.bookingTerms, ...change } }));

  const handleSave = async () => {
    try {
      const updated = await saveOffer.mutateAsync({
        name: changed.name ? form.name.trim() : undefined,
        content: changed.content ? form.content : undefined,
        bookingTerms: changed.bookingTerms ? form.bookingTerms : undefined,
        configurations: Object.fromEntries(
          changedConfigurationIds.map((id) => [id, cleanConfigurationContent(form.configurations[id])]),
        ),
      });
      if (updated) setForm(formFrom(updated));
      enqueueSnackbar('Content saved', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' });
    }
  };

  return (
    <Box>
      <Stack spacing={3}>
        <Section title="Name" description="The package name customers see.">
          <TextField
            label="Name"
            value={form.name}
            onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
            error={!form.name.trim()}
            required
            fullWidth
          />
        </Section>

        <Section title="Tour details">
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              {enumField('Tour style', TOUR_STYLE_LABELS, form.content.tourStyle, (tourStyle) =>
                setContent({ tourStyle }),
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              {enumField('Guide type', GUIDE_TYPE_LABELS, form.content.guideType, (guideType) =>
                setContent({ guideType }),
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              {enumField('Activity level', ACTIVITY_LEVEL_LABELS, form.content.activityLevel, (activityLevel) =>
                setContent({ activityLevel }),
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={form.content.categories}
                onChange={(_event, categories) => setContent({ categories: categories as Array<string> })}
                renderTags={(values, getTagProps) =>
                  values.map((value, index) => (
                    <Chip {...getTagProps({ index })} key={value} size="small" label={value} />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Categories" helperText="Press Enter to add a category" />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Operated by"
                value={form.content.operatedBy ?? ''}
                onChange={(event) => setContent({ operatedBy: nullIfEmpty(event.target.value) })}
                fullWidth
              />
            </Grid>
          </Grid>
        </Section>

        <Section
          title="Fine print"
          description="What's not included, important information and the rest of the fine print on the page."
        >
          <FinePrintEditor items={form.content.finePrint} onChange={(finePrint) => setContent({ finePrint })} />
        </Section>

        <Section title="Joining and meeting">
          <Stack spacing={2}>
            <TextField
              label="Joining instructions"
              value={form.content.joiningInstructions ?? ''}
              onChange={(event) => setContent({ joiningInstructions: nullIfEmpty(event.target.value) })}
              multiline
              minRows={2}
              fullWidth
            />
            <TextField
              label="Meeting instructions"
              value={form.content.meetingInstructions ?? ''}
              onChange={(event) => setContent({ meetingInstructions: nullIfEmpty(event.target.value) })}
              multiline
              minRows={2}
              fullWidth
            />
          </Stack>
        </Section>

        <Section title="Deposit and payment" description="Payment schedule templates are kept as they are.">
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 3 }}>
              {enumField('Deposit type', DEPOSIT_TYPE_LABELS, form.bookingTerms.depositType, (depositType) =>
                setBookingTerms({ depositType }),
              )}
            </Grid>
            {(
              [
                ['depositAmount', 'Deposit amount'],
                ['depositDueDays', 'Deposit due (days)'],
                ['finalPaymentDueDays', 'Final payment due (days)'],
              ] as const
            ).map(([field, label]) => (
              <Grid key={field} size={{ xs: 12, sm: 3 }}>
                <TextField
                  type="number"
                  label={label}
                  value={form.bookingTerms[field] ?? ''}
                  onChange={(event) => setBookingTerms({ [field]: numberOrNull(event.target.value) })}
                  inputProps={{ min: 0 }}
                  fullWidth
                />
              </Grid>
            ))}
          </Grid>
        </Section>

        <Section
          title="About, highlights and itinerary"
          description="Copied from the base tour when the bundle was approved. Edits change this bundle only, never the base tour."
        >
          {configurations.length === 0 ? (
            <Typography color="text.secondary">No tour configurations.</Typography>
          ) : (
            <Stack spacing={1}>
              {configurations.map((configuration) => (
                <ConfigurationEditor
                  key={configuration.id}
                  configuration={configuration}
                  value={form.configurations[configuration.id]}
                  onChange={(value) =>
                    setForm((current) => ({
                      ...current,
                      configurations: { ...current.configurations, [configuration.id]: value },
                    }))
                  }
                  isChanged={changedConfigurationIds.includes(configuration.id)}
                  defaultExpanded={configurations.length === 1}
                />
              ))}
            </Stack>
          )}
        </Section>
      </Stack>

      {changeCount > 0 && (
        <Paper
          elevation={6}
          sx={{ position: 'sticky', bottom: 16, mt: 2, px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}
        >
          <Typography flex={1}>
            {invalid ? 'Fix the highlighted fields to save.' : 'You have unsaved content changes.'}
          </Typography>
          <Button onClick={() => setForm(saved)} disabled={saveOffer.isPending}>
            Discard
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={invalid || saveOffer.isPending}>
            Save
          </Button>
        </Paper>
      )}
    </Box>
  );
}
